/**
 * Feature Flags — central ON/OFF for progressive enhancements.
 *
 * Two ways to use:
 *   1. Static (non-reactive):  import { ff } from "@/lib/feature-flags";
 *   2. Reactive (React):       import { useFeatureFlags } from "@/lib/feature-flags";
 *
 * Toggle at build time via VITE_FF_* env vars, or at runtime via
 * the Settings panel (no reload needed with useFeatureFlags).
 *
 * Runtime overrides are stored as individual localStorage keys:
 *   ff_override_pwa = "true" | "false"
 */

import { create } from "zustand";

export interface FeatureFlags {
  /** Show skeleton placeholders instead of spinners */
  skeletonScreens: boolean;
  /** PWA: service worker + offline page + install prompt */
  pwa: boolean;
  /** Dynamic SEO: per-page meta tags, JSON-LD, Open Graph */
  seo: boolean;
  /** Lazy-load images below the fold via IntersectionObserver */
  imageLazyLoading: boolean;
  /** Optimistic UI: update cart/UI instantly before server confirms */
  optimisticUpdates: boolean;
  /** Send expected_unit_price to server for price-tamper protection */
  serverPriceValidation: boolean;
  /** Supabase Realtime: live stock counts and order status */
  realtimeUpdates: boolean;
  /** Pre-render SEO-critical pages at build time */
  ssr: boolean;
  /** E2E test hooks: add data-testid attributes to key elements */
  e2eTestIds: boolean;
}

/** Human-readable labels and descriptions for each flag */
export const FLAG_META: Record<keyof FeatureFlags, { label: string; description: string }> = {
  skeletonScreens: {
    label: "Skeleton Screens",
    description: "Affiche des placeholders animés pendant le chargement (style Amazon)",
  },
  pwa: {
    label: "PWA / Mode hors-ligne",
    description: "Service worker + page hors-ligne + installation sur l'écran d'accueil",
  },
  seo: {
    label: "SEO dynamique",
    description: "Balises meta, Open Graph, JSON-LD et canonical par page",
  },
  imageLazyLoading: {
    label: "Chargement paresseux des images",
    description: "Les images sous le fold se chargent uniquement quand elles deviennent visibles",
  },
  optimisticUpdates: {
    label: "Mises à jour optimistes",
    description: "Le panier se met à jour instantanément sans attendre le serveur",
  },
  serverPriceValidation: {
    label: "Validation des prix serveur",
    description: "Envoie le prix attendu au serveur pour détecter les modifications",
  },
  realtimeUpdates: {
    label: "Mises à jour temps réel",
    description: "Stock et statut de commande actualisés en direct via Supabase",
  },
  ssr: {
    label: "Pré-rendu SEO",
    description: "Sitemap et méta-données générés au build pour les moteurs de recherche",
  },
  e2eTestIds: {
    label: "Attributs de test E2E",
    description: "Ajoute des data-testid sur les éléments clés pour Playwright",
  },
};

function envFlag(name: string, defaultValue: boolean): boolean {
  const envKey = `VITE_FF_${name.toUpperCase()}`;
  const envVal = (import.meta as Record<string, Record<string, string>>).env?.[envKey];
  if (envVal === "true") return true;
  if (envVal === "false") return false;
  return defaultValue;
}

/** Build-time defaults (from env vars or hardcoded true) */
const DEFAULTS: FeatureFlags = {
  skeletonScreens: envFlag("skeleton_screens", true),
  pwa: envFlag("pwa", true),
  seo: envFlag("seo", true),
  imageLazyLoading: envFlag("image_lazy_loading", true),
  optimisticUpdates: envFlag("optimistic_updates", true),
  serverPriceValidation: envFlag("server_price_validation", true),
  realtimeUpdates: envFlag("realtime_updates", true),
  ssr: envFlag("ssr", true),
  e2eTestIds: envFlag("e2e_test_ids", true),
};

const FLAG_KEYS = Object.keys(DEFAULTS) as Array<keyof FeatureFlags>;

/** Read all overrides from individual localStorage keys */
function readStorageOverrides(): Partial<Record<keyof FeatureFlags, boolean>> {
  const overrides: Partial<Record<keyof FeatureFlags, boolean>> = {};
  if (typeof localStorage === "undefined") return overrides;
  for (const key of FLAG_KEYS) {
    const raw = localStorage.getItem(`ff_override_${key}`);
    if (raw === "true") overrides[key] = true;
    else if (raw === "false") overrides[key] = false;
  }
  return overrides;
}

/** Resolve overrides against defaults into a full FeatureFlags object */
function resolveFlags(overrides: Partial<Record<keyof FeatureFlags, boolean>>): FeatureFlags {
  return { ...DEFAULTS, ...overrides };
}

// ---------------------------------------------------------------------------
// Reactive Zustand store (syncs to individual localStorage keys)
// ---------------------------------------------------------------------------

interface FlagStore {
  flags: FeatureFlags;
  setFlag: (name: keyof FeatureFlags, value: boolean) => void;
  resetFlag: (name: keyof FeatureFlags) => void;
  resetAll: () => void;
  enableAll: () => void;
  disableAll: () => void;
}

export const useFlagStore = create<FlagStore>()((set) => ({
  flags: resolveFlags(readStorageOverrides()),

  setFlag: (name, value) => {
    localStorage.setItem(`ff_override_${name}`, String(value));
    set((state) => ({ flags: { ...state.flags, [name]: value } }));
  },

  resetFlag: (name) => {
    localStorage.removeItem(`ff_override_${name}`);
    set((state) => ({ flags: { ...state.flags, [name]: DEFAULTS[name] } }));
  },

  resetAll: () => {
    for (const key of FLAG_KEYS) {
      localStorage.removeItem(`ff_override_${key}`);
    }
    set({ flags: { ...DEFAULTS } });
  },

  enableAll: () => {
    for (const key of FLAG_KEYS) {
      localStorage.setItem(`ff_override_${key}`, "true");
    }
    const all = {} as Record<keyof FeatureFlags, boolean>;
    for (const key of FLAG_KEYS) all[key] = true;
    set({ flags: all as FeatureFlags });
  },

  disableAll: () => {
    for (const key of FLAG_KEYS) {
      localStorage.setItem(`ff_override_${key}`, "false");
    }
    const all = {} as Record<keyof FeatureFlags, boolean>;
    for (const key of FLAG_KEYS) all[key] = false;
    set({ flags: all as FeatureFlags });
  },
}));

// ---------------------------------------------------------------------------
// React hook — use in components for reactive updates
// ---------------------------------------------------------------------------

/** Reactive feature flags — re-renders when any flag changes */
export function useFeatureFlags(): FeatureFlags {
  return useFlagStore((s) => s.flags);
}

// ---------------------------------------------------------------------------
// Static access — frozen snapshot built at module load time
// ---------------------------------------------------------------------------

/** Immutable snapshot of flags at import time (reads from localStorage) */
export const ff: Readonly<FeatureFlags> = Object.freeze(
  resolveFlags(readStorageOverrides())
);

/** List all flags with current values (reactive store, not frozen snapshot) */
export function listFlags(): Record<string, boolean> {
  return { ...useFlagStore.getState().flags };
}

/** Toggle a single flag at runtime (writes to localStorage) */
export function setFlagOverride(name: keyof FeatureFlags, value: boolean): void {
  useFlagStore.getState().setFlag(name, value);
}

/** Clear a single runtime override (removes from localStorage) */
export function clearFlagOverride(name: keyof FeatureFlags): void {
  useFlagStore.getState().resetFlag(name);
}

/** Clear all runtime overrides (removes all ff_override_* from localStorage) */
export function clearAllOverrides(): void {
  useFlagStore.getState().resetAll();
}
